import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button, CircularProgress, LinearProgress, Snackbar, Typography
} from '@mui/material'
import Drawer from 'components/drawer/detail'
import CONSTANT from 'modules/constant'
import {
  useDownloadRowsMutation,
  useGetInjectionJobDetailQuery,
  useGetInjectionJobRowsQuery, useRetryFailedInjectionMutation
} from 'store/carInjection'
import GLOBAL from "modules/global"
import { useGetConfigItemsMutation } from "store/configItem"

// ── Detail Drawer Content ──────────────────────────────────────────

interface JobDetailDrawerProps {
  jobId: string
  onClose: () => void
}

const rowTableHeadTitles = [
  'Row',
  'Member Code',
  'VIN',
  'Status',
  'Failure Code',
  'Failure Message',
  'Processed At'
]

const configFormData = new FormData()
configFormData.append('search[value]', 'CONFIG_ENABLE_RETRY_INJECTION')

const POLLING_INTERVAL = 10000

export const JobDetailDrawer = ({ jobId, onClose }: JobDetailDrawerProps) => {
  const [retryFailed, retryResult] = useRetryFailedInjectionMutation()
  const [downloadRows, downloadResult] = useDownloadRowsMutation()
  const [getConfigItems, configItem] = useGetConfigItemsMutation()
  const enableRetry = configItem?.data?.data?.[0]
  const [snackbar, setSnackbar] = useState({ open: false, message: '', isError: false })

  useEffect(() => {
    getConfigItems(configFormData)
  }, [])

  const {
    data: job,
    isLoading: jobLoading,
    refetch: refetchJob
  } = useGetInjectionJobDetailQuery(jobId, { skip: !jobId })

  const [payload, setPayload] = useState({
    page: 1,
    pageSize: 20,
    status: ''
  })

  const rowsResult = useGetInjectionJobRowsQuery(
    {
      jobId,
      status: payload.status || undefined,
      page: payload.page,
      pageSize: payload.pageSize
    },
  )

  useEffect(() => {
    const interval = setInterval(() => {
      if (jobId && document.hasFocus() && !jobLoading) {
        refetchJob()
        rowsResult.refetch()
      }
    }, POLLING_INTERVAL)

    return () => clearInterval(interval)
  }, [jobId, jobLoading])

  const refetchAll = () => {
    refetchJob()
  }

  useEffect(() => {
    setPayload({ page: 1, pageSize: 20, status: '' })
  }, [jobId])

  const handleRetryFailed = async () => {
    try {
      await retryFailed(jobId).unwrap()
      setSnackbar({ open: true, message: 'Retry initiated', isError: false })
      refetchAll()
      onClose()
    } catch {
      setSnackbar({
        open: true,
        message: 'Retry failed - another job may be processing',
        isError: true
      })
    }
  }

  const handleDownloadRows = async () => {
    try {
      await downloadRows(jobId).unwrap()
      setSnackbar({ open: true, message: 'Download initiated', isError: false })
    } catch (e) {
      setSnackbar({
        open: true,
        message: `Download failed - ${e}`,
        isError: true
      })
    }
  }

  const totalRows = rowsResult.data?.totalRecord ?? 0
  const rowsOnPage = rowsResult.data?.rows?.length ?? 0
  const firstRowNumber = rowsOnPage ? (payload.page - 1) * payload.pageSize + 1 : 0
  const lastRowNumber = rowsOnPage ? firstRowNumber + rowsOnPage - 1 : 0
  const totalPage = rowsResult.data ? Math.ceil(rowsResult.data.totalRecord / payload.pageSize) : 1
  const progress = job && job.totalRows > 0 ? (job.successRows / job.totalRows) * 100 : 0

  return (
    <Drawer open title='View File Detail' maxWidth='md' onClose={onClose}>
      <Box sx={{ px: 3, pb: 3 }}>
        {jobLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : job ? (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                rowGap: 2,
                columnGap: 3,
                mb: 2.5
              }}
            >
              <Box>
                <Typography variant='caption' sx={{ color: '#667085', textTransform: 'uppercase' }}>
                  File ID
                </Typography>
                <Typography variant='body1' fontWeight={700}>
                  {job.jobId || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' sx={{ color: '#667085', textTransform: 'uppercase' }}>
                  Status
                </Typography>
                <Typography
                  variant='body1'
                  fontWeight={700}
                  sx={{ color: getStatusTextColor(job.status) }}
                >
                  {formatStatusLabel(job.status)}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' sx={{ color: '#667085', textTransform: 'uppercase' }}>
                  Uploaded By
                </Typography>
                <Typography variant='body1' fontWeight={700}>
                  {job.createdBy || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' sx={{ color: '#667085', textTransform: 'uppercase' }}>
                  Uploaded Date
                </Typography>
                <Typography variant='body1' fontWeight={700}>
                  {GLOBAL.formatDate(job.createdAt)}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' sx={{ color: '#667085', textTransform: 'uppercase' }}>
                  Filename
                </Typography>
                <Typography variant='body2'>{job.sourceFilename || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant='caption' sx={{ color: '#667085', textTransform: 'uppercase' }}>
                  Summary
                </Typography>
                <Typography variant='body2'>
                  Total: {job.totalRows} | Success: {job.successRows} | Failed: {job.failedRows}
                </Typography>
              </Box>
            </Box>

            {(job.status === 'PROCESSING' || job.status === 'UPLOADED') && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <LinearProgress
                  variant='determinate'
                  value={progress}
                  sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                />
                <Typography variant='body2'>{Math.round(progress)}%</Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {enableRetry?.value === 'true' ? <Button
                variant='contained'
                size='small'
                onClick={handleRetryFailed}
                disabled={
                  retryResult.isLoading ||
                  (job?.failedRows ?? 0) === 0 ||
                  job.status === 'PROCESSING'
                }
              >
                Retry Failed
              </Button> : null}
              <Button
                variant='outlined'
                size='small'
                onClick={handleDownloadRows}
                disabled={downloadResult.isLoading}
              >
                Download File
              </Button>
            </Box>
          </>
        ) : null}

        <Box
          sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}
        >
          <Typography variant='h6' sx={{ fontSize: 24, fontWeight: 700 }}>
            File contents ({totalRows} rows)
          </Typography>
          <Typography variant='body2' sx={{ color: '#98A2B3' }}>
            Displaying {firstRowNumber}-{lastRowNumber} of {totalRows} rows
          </Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #EAECF0' }}>
                {rowTableHeadTitles.map((t) => (
                  <th
                    key={t}
                    style={{
                      padding: '10px 6px',
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                      color: '#667085',
                      fontWeight: 500
                    }}
                  >
                    {t}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rowsResult.isLoading ? (
                <tr>
                  <td
                    colSpan={rowTableHeadTitles.length}
                    style={{ textAlign: 'center', padding: 16 }}
                  >
                    <CircularProgress size={20} />
                  </td>
                </tr>
              ) : rowsResult.data?.rows?.length ? (
                rowsResult.data.rows.map((row) => (
                  <tr key={row.id} style={{ borderBottom: '1px solid #F2F4F7' }}>
                    <td style={{ padding: '10px 6px' }}>{row.lineNumber}</td>
                    <td style={{ padding: '10px 6px', fontWeight: 600 }}>{row.memberId || '-'}</td>
                    <td style={{ padding: '10px 6px', fontWeight: 600 }}>{row.vin || '-'}</td>
                    <td style={{ padding: '10px 6px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      <span style={{ color: getStatusTextColor(row.status) }}>
                        {formatStatusLabel(row.status)}
                      </span>
                    </td>
                    <td style={{ padding: '10px 6px' }}>{row.failureCode || '-'}</td>
                    <td
                      style={{
                        padding: '10px 6px',
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {row.failureMessage || '-'}
                    </td>
                    <td style={{ padding: '10px 6px', whiteSpace: 'nowrap' }}>
                      {GLOBAL.formatDateWithTime(row.processedAt || '')}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={rowTableHeadTitles.length}
                    style={{ textAlign: 'center', padding: 16, color: '#999' }}
                  >
                    No rows
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>

        {totalPage > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
            <Button
              size='small'
              disabled={payload.page <= 1}
              onClick={() => setPayload({ ...payload, page: payload.page - 1 })}
            >
              Prev
            </Button>
            <Typography variant='body2' sx={{ alignSelf: 'center' }}>
              Page {payload.page} of {totalPage}
            </Typography>
            <Button
              size='small'
              disabled={payload.page >= totalPage}
              onClick={() => setPayload({ ...payload, page: payload.page + 1 })}
            >
              Next
            </Button>
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={CONSTANT.NOTIFICATION_DURATION}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.isError ? 'error' : 'success'}>{snackbar.message}</Alert>
      </Snackbar>
    </Drawer>
  )
}

const formatStatusLabel = (status?: string) => status?.replaceAll('_', ' ') || '-'
const getStatusTextColor = (status?: string) => {
  if (status === 'FAILED') return '#D92D20'
  if (status === 'COMPLETED' || status === 'ATTACHED') return '#16A34A'
  if (status === 'PARTIAL_FAILED') return '#F59E0B'
  return '#667085'
}
